import API from "../../services/api";
import React, { useEffect, useState } from "react";
import {
  FaCheckCircle,
  FaEdit,
  FaUser
} from "react-icons/fa";
import { Link, useSearchParams } from "react-router-dom";
import CandidateHeader from "./CandidateHeader";
import "./CandidateDashboard.css";

const ALLOWED_TABS = ["overview", "applications", "interviews", "profile"];
const EMAIL_CHANGE_OTP_VALIDITY_MS = 10 * 60 * 1000;
const EMAIL_CHANGE_RESEND_COOLDOWN_SECONDS = 30;

const getResendCooldownFromExpiry = (otpExpiry) => {
  if (!otpExpiry) {
    return 0;
  }

  const expiryTimestamp = new Date(otpExpiry).getTime();

  if (Number.isNaN(expiryTimestamp)) {
    return 0;
  }

  const sentTimestamp = expiryTimestamp - EMAIL_CHANGE_OTP_VALIDITY_MS;
  const resendAvailableAt =
    sentTimestamp + EMAIL_CHANGE_RESEND_COOLDOWN_SECONDS * 1000;

  return Math.max(0, Math.ceil((resendAvailableAt - Date.now()) / 1000));
};

const CandidateDashboard = () => {
  const [applications, setApplications] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();

  const tabFromUrl = searchParams.get("tab");
  const viewFromUrl = searchParams.get("view");
  const initialTab = ALLOWED_TABS.includes(tabFromUrl) ? tabFromUrl : "overview";
  const [activeTab, setActiveTab] = useState(initialTab);
  const isProfileOnlyView =
    tabFromUrl === "profile" && viewFromUrl === "profile-only";

  const [candidateProfile, setCandidateProfile] = useState({
    name: "",
    email: "",
    pendingEmail: "",
    pendingEmailOtpExpiry: "",
    emailChangePending: false
  });
  const [profileDraft, setProfileDraft] = useState({
    name: "",
    email: ""
  });
  const [editingField, setEditingField] = useState(null);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileFeedback, setProfileFeedback] = useState("");
  const [emailChangeOtp, setEmailChangeOtp] = useState("");
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
  const [isResendingEmailOtp, setIsResendingEmailOtp] = useState(false);
  const [isCancellingEmailChange, setIsCancellingEmailChange] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    const fetchApplications = async () => {
      try {
        const res = await API.get("/applications/my");
        setApplications(res.data.data || []);
      } catch (error) {
        console.error("Fetch applications error:", error);
      }
    };

    fetchApplications();
  }, []);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await API.get("/client/candidate/profile");

        setCandidateProfile({
          name: res.data.data?.username || "",
          email: res.data.data?.email || "",
          pendingEmail: res.data.data?.pendingEmail || "",
          pendingEmailOtpExpiry: res.data.data?.pendingEmailOtpExpiry || "",
          emailChangePending: Boolean(res.data.data?.emailChangePending)
        });
        setProfileDraft({
          name: res.data.data?.username || "",
          email: res.data.data?.email || ""
        });
        setEmailChangeOtp("");
        setResendCooldown(
          getResendCooldownFromExpiry(res.data.data?.pendingEmailOtpExpiry)
        );
      } catch (error) {
        console.error("Profile fetch error:", error);
      }
    };

    fetchProfile();
  }, []);

  useEffect(() => {
    const nextTab = searchParams.get("tab");
    if (ALLOWED_TABS.includes(nextTab) && nextTab !== activeTab) {
      setActiveTab(nextTab);
    }
  }, [searchParams, activeTab]);

  useEffect(() => {
    if (resendCooldown <= 0) {
      return undefined;
    }

    const timer = setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const handleEditToggle = (field) => {
    setProfileFeedback("");
    setEditingField(field);
    setProfileDraft((prev) => ({
      ...prev,
      [field]: candidateProfile[field] || ""
    }));
  };

  const handleProfileInputChange = (field, value) => {
    setProfileDraft((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleProfileCancel = () => {
    setEditingField(null);
    setProfileFeedback("");
    setEmailChangeOtp("");
    setProfileDraft({
      name: candidateProfile.name || "",
      email: candidateProfile.email || ""
    });
  };

  const handleProfileSave = async (field) => {
    const nextName =
      field === "name" ? profileDraft.name.trim() : candidateProfile.name;
    const nextEmail =
      field === "email" ? profileDraft.email.trim() : candidateProfile.email;

    if (!nextName) {
      setProfileFeedback("Name cannot be empty.");
      return;
    }

    if (!nextEmail) {
      setProfileFeedback("Email cannot be empty.");
      return;
    }

    try {
      setIsSavingProfile(true);
      setProfileFeedback("");

      const res = await API.put("/client/candidate/profile", {
        name: nextName,
        email: nextEmail
      });

      const updatedProfile = {
        name: res.data.data?.username || nextName,
        email: res.data.data?.email || candidateProfile.email,
        pendingEmail: res.data.data?.pendingEmail || "",
        pendingEmailOtpExpiry: res.data.data?.pendingEmailOtpExpiry || "",
        emailChangePending: Boolean(res.data.data?.emailChangePending)
      };

      setCandidateProfile(updatedProfile);
      setProfileDraft({
        name: updatedProfile.name,
        email: updatedProfile.email
      });
      setEditingField(null);
      setEmailChangeOtp("");
      setResendCooldown(
        res.data.requiresEmailVerification
          ? getResendCooldownFromExpiry(res.data.data?.pendingEmailOtpExpiry)
          : 0
      );
      setProfileFeedback(
        res.data.requiresEmailVerification
          ? res.data.message
          : "Profile updated successfully."
      );
    } catch (error) {
      console.error("Profile update error:", error);
      setProfileFeedback(
        error.response?.data?.message || "Unable to update profile right now."
      );
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleVerifyEmailChange = async () => {
    if (!emailChangeOtp.trim()) {
      setProfileFeedback("Enter the OTP sent to your new email.");
      return;
    }

    try {
      setIsVerifyingEmail(true);
      setProfileFeedback("");

      const res = await API.post("/client/candidate/profile/verify-email-change", {
        otp: emailChangeOtp.trim()
      });

      const updatedProfile = {
        name: res.data.data?.username || candidateProfile.name,
        email: res.data.data?.email || candidateProfile.email,
        pendingEmail: "",
        pendingEmailOtpExpiry: "",
        emailChangePending: false
      };

      setCandidateProfile(updatedProfile);
      setProfileDraft({
        name: updatedProfile.name,
        email: updatedProfile.email
      });
      setEmailChangeOtp("");
      setEditingField(null);
      setResendCooldown(0);
      setProfileFeedback("Email updated successfully.");
    } catch (error) {
      console.error("Verify email change error:", error);
      setProfileFeedback(
        error.response?.data?.message || "Unable to verify OTP right now."
      );
    } finally {
      setIsVerifyingEmail(false);
    }
  };

  const handleResendEmailOtp = async () => {
    try {
      setIsResendingEmailOtp(true);
      setProfileFeedback("");

      const res = await API.post(
        "/client/candidate/profile/resend-email-change-otp"
      );

      setResendCooldown(EMAIL_CHANGE_RESEND_COOLDOWN_SECONDS);
      setProfileFeedback(res.data.message || "OTP resent successfully.");
    } catch (error) {
      console.error("Resend email change OTP error:", error);
      setProfileFeedback(
        error.response?.data?.message || "Unable to resend OTP right now."
      );
    } finally {
      setIsResendingEmailOtp(false);
    }
  };

  const handleCancelEmailChange = async () => {
    try {
      setIsCancellingEmailChange(true);
      setProfileFeedback("");

      const res = await API.delete("/client/candidate/profile/cancel-email-change");

      setCandidateProfile((prev) => ({
        ...prev,
        pendingEmail: "",
        pendingEmailOtpExpiry: "",
        emailChangePending: false
      }));
      setEmailChangeOtp("");
      setResendCooldown(0);
      setProfileFeedback(
        res.data.message || "Pending email change cancelled successfully."
      );
    } catch (error) {
      console.error("Cancel email change error:", error);
      setProfileFeedback(
        error.response?.data?.message || "Unable to cancel email change right now."
      );
    } finally {
      setIsCancellingEmailChange(false);
    }
  };

  const interviewJobs = applications.filter(
    (a) => a.status === "INTERVIEW_SCHEDULED"
  );

  const stats = {
    total: applications.length,
    shortlisted: applications.filter((a) => a.status === "SHORTLISTED").length,
    interview: interviewJobs.length,
    rejected: applications.filter((a) => a.status === "REJECTED").length
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "APPLIED":
        return "status-applied";
      case "SHORTLISTED":
        return "status-shortlisted";
      case "OFFER_RELEASED":
        return "status-offer-released";
      case "SELECTED":
        return "status-selected";
      case "INTERVIEW_SCHEDULED":
        return "status-interview";
      case "INTERVIEW_COMPLETED":
        return "status-completed";
      case "REJECTED":
        return "status-rejected";
      default:
        return "";
    }
  };

  const renderEmptyState = (message) => (
    <div className="empty-state">
      <p>{message}</p>
    </div>
  );

  return (
    <>
      <CandidateHeader withSpacer />

      <div className="candidate-dashboard-page">
        <main className="candidate-main">
          {isProfileOnlyView && (
            <div className="back-btn-container">
              <Link to="/candidate/candidatecareer" className="back-link-dashboard">
                &larr; Back to Careers
              </Link>
            </div>
          )}

          {!isProfileOnlyView && (
            <>
              <div className="back-btn-container">
                <Link to="/candidate/candidatecareer" className="back-link-dashboard">
                  &larr; Back to Careers
                </Link>
              </div>

              <header className="candidate-header">
                <h1>Welcome, {candidateProfile.name}</h1>
                <p>Track your applications, interviews, and profile details here.</p>
              </header>

              <div className="candidate-tabs-panel">
                <div className="candidate-tabs">
                  {ALLOWED_TABS.map((tab) => (
                    <button
                      key={tab}
                      className={`candidate-menu ${activeTab === tab ? "active" : ""}`}
                      onClick={() => handleTabChange(tab)}
                    >
                      {tab === "overview"
                        ? "Overview"
                        : tab === "applications"
                        ? "My Applications"
                        : tab === "interviews"
                        ? "Interview Schedule"
                        : "Profile"}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          <section className="candidate-content-card">
            {activeTab === "overview" && (
              <div className="stats-grid">
                <div className="stat-card">
                  <p>Total Applications</p>
                  <h3>{stats.total}</h3>
                </div>
                <div className="stat-card">
                  <p>Shortlisted</p>
                  <h3>{stats.shortlisted}</h3>
                </div>
                <div className="stat-card">
                  <p>Interview Scheduled</p>
                  <h3>{stats.interview}</h3>
                </div>
                <div className="stat-card">
                  <p>Rejected</p>
                  <h3>{stats.rejected}</h3>
                </div>
              </div>
            )}

            {activeTab === "applications" && (
              <section className="table-section">
                <h2>My Applications</h2>

                {applications.length === 0 ? (
                  renderEmptyState("You have not applied to any roles yet.")
                ) : (
                  <table className="candidate-table">
                    <thead>
                      <tr>
                        <th>Job Title</th>
                        <th>Date Applied</th>
                        <th>Status</th>
                        <th>Resume</th>
                      </tr>
                    </thead>

                    <tbody>
                      {applications.map((job) => (
                        <tr key={job.id}>
                          <td>{job.jobTitle}</td>
                          <td>{job.appliedDate}</td>
                          <td>
                            <span className={`badge ${getStatusClass(job.status)}`}>
                              {job.status?.replaceAll("_", " ")}
                            </span>
                          </td>
                          <td>{job.resumeName || "Uploaded"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>
            )}

            {activeTab === "interviews" && (
              <section className="table-section">
                <h2>Interview Schedule</h2>

                {interviewJobs.length === 0 ? (
                  renderEmptyState("No interviews are scheduled right now.")
                ) : (
                  <table className="candidate-table">
                    <thead>
                      <tr>
                        <th>Job Title</th>
                        <th>Date</th>
                        <th>Time</th>
                        <th>Type</th>
                        <th>Details</th>
                      </tr>
                    </thead>

                    <tbody>
                      {interviewJobs.map((job) => (
                        <tr key={job.id}>
                          <td>{job.jobTitle}</td>
                          <td>{job.interview?.date || "To be announced"}</td>
                          <td>{job.interview?.time || "To be announced"}</td>
                          <td>{job.interview?.interviewType || "N/A"}</td>
                          <td>
                            {job.interview?.interviewType === "OFFLINE" ? (
                              <span>
                                {job.interview?.location || "Location not provided"}
                              </span>
                            ) : job.interview?.meetingLink ? (
                              <a
                                href={job.interview.meetingLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="meeting-link"
                              >
                                Join Meeting
                              </a>
                            ) : (
                              "N/A"
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>
            )}

            {activeTab === "profile" && (
              <section className="profile-card">
                <h2>Profile Details</h2>

                <div className="profile-row">
                  <div className="profile-row-main">
                    <span className="profile-row-label">
                      <FaUser /> Name
                    </span>

                    {editingField === "name" ? (
                      <div className="profile-edit-controls">
                        <input
                          type="text"
                          value={profileDraft.name}
                          onChange={(e) =>
                            handleProfileInputChange("name", e.target.value)
                          }
                          className="profile-edit-input"
                          disabled={isSavingProfile}
                        />
                        <button
                          type="button"
                          className="profile-action-btn save"
                          onClick={() => handleProfileSave("name")}
                          disabled={isSavingProfile}
                        >
                          {isSavingProfile ? "Saving..." : "Save"}
                        </button>
                        <button
                          type="button"
                          className="profile-action-btn cancel"
                          onClick={handleProfileCancel}
                          disabled={isSavingProfile}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="profile-row-value-wrap">
                        <span>{candidateProfile.name}</span>
                        <button
                          type="button"
                          className="profile-icon-btn"
                          onClick={() => handleEditToggle("name")}
                        >
                          <FaEdit /> Edit
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="profile-row">
                  <div className="profile-row-main">
                    <span className="profile-row-label">
                      <FaCheckCircle /> Email
                    </span>

                    {editingField === "email" ? (
                      <div className="profile-edit-controls">
                        <input
                          type="email"
                          value={profileDraft.email}
                          onChange={(e) =>
                            handleProfileInputChange("email", e.target.value)
                          }
                          className="profile-edit-input"
                          disabled={isSavingProfile}
                        />
                        <button
                          type="button"
                          className="profile-action-btn save"
                          onClick={() => handleProfileSave("email")}
                          disabled={isSavingProfile}
                        >
                          {isSavingProfile ? "Saving..." : "Save"}
                        </button>
                        <button
                          type="button"
                          className="profile-action-btn cancel"
                          onClick={handleProfileCancel}
                          disabled={isSavingProfile}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="profile-row-value-wrap">
                        <span>{candidateProfile.email}</span>
                        <button
                          type="button"
                          className="profile-icon-btn"
                          onClick={() => handleEditToggle("email")}
                        >
                          <FaEdit /> Edit
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {candidateProfile.emailChangePending && (
                  <div className="profile-pending-box">
                    <p className="profile-pending-title">
                      Pending email change: {candidateProfile.pendingEmail}
                    </p>
                    <p className="profile-pending-copy">
                      We sent an OTP to your new email. Enter it below to finish the update, or cancel this request if it was from an earlier session.
                    </p>
                    <div className="profile-otp-actions">
                      <input
                        type="text"
                        value={emailChangeOtp}
                        onChange={(e) => setEmailChangeOtp(e.target.value)}
                        className="profile-edit-input"
                        placeholder="Enter OTP"
                        disabled={isVerifyingEmail}
                      />
                      <button
                        type="button"
                        className="profile-action-btn save"
                        onClick={handleVerifyEmailChange}
                        disabled={isVerifyingEmail}
                      >
                        {isVerifyingEmail ? "Verifying..." : "Verify OTP"}
                      </button>
                      <button
                        type="button"
                        className="profile-action-btn cancel"
                        onClick={handleResendEmailOtp}
                        disabled={isResendingEmailOtp || resendCooldown > 0}
                      >
                        {isResendingEmailOtp
                          ? "Sending..."
                          : resendCooldown > 0
                          ? `Resend OTP in ${resendCooldown}s`
                          : "Resend OTP"}
                      </button>
                      <button
                        type="button"
                        className="profile-action-btn cancel-email"
                        onClick={handleCancelEmailChange}
                        disabled={isCancellingEmailChange}
                      >
                        {isCancellingEmailChange ? "Cancelling..." : "Cancel Request"}
                      </button>
                    </div>
                    {resendCooldown > 0 && (
                      <p className="profile-otp-timer">
                        You can request another OTP in {resendCooldown} seconds.
                      </p>
                    )}
                  </div>
                )}

                {profileFeedback && (
                  <p className="profile-feedback-message">{profileFeedback}</p>
                )}
              </section>
            )}
          </section>
        </main>
      </div>
    </>
  );
};

export default CandidateDashboard;
